const giftConfig = Theme?.consoGiftProgress;

const parseGiftTiers = (config) => {
  return (config?.tiers ?? []).filter(
    (tier) => Number(tier?.metaCents) > 0 && Array.isArray(tier?.brindes) && tier.brindes.length > 0
  );
};

const initialConfig = giftConfig?.config;
const initialGiftTiers = parseGiftTiers(initialConfig);

if (giftConfig?.enabled && initialGiftTiers.length > 0) {
  const AUTO_GIFT_SOURCE = 'conso-cart-gift-auto-add';
  const AUTO_GIFT_PROPERTY_KEY = giftConfig.propertyKey;
  const AUTO_GIFT_PROPERTY_VALUE = String(giftConfig.propertyValue ?? 'true').toLowerCase();
  const QUALIFYING_TOTAL_ATTRIBUTE_KEY = giftConfig.qualifyingTotalAttributeKey;

  /** @type {Promise<void> | null} */
  let syncInFlight = null;
  // Gatilho que chegou com um sync em andamento. Ver syncGiftPresence.
  let syncPendente = false;
  /** @type {number | null} */
  let scheduledSync = null;
  /** @type {any} */
  let queuedCart = null;
  let manualActionInFlight = false;

  // Marca da linha de brinde. 'true' é a marca sem tier (modo não-cumulativo e
  // carrinho aberto antes desta versão); 'm<metaCents>' amarra a linha ao tier
  // daquela meta, que é o que o modo cumulativo precisa para saber QUAL brinde a
  // linha está reivindicando. A function `brinde` lê as duas.
  const parseMarker = (value) => {
    const raw = String(value ?? '')
      .trim()
      .toLowerCase();

    if (!raw) return null;
    if (raw === AUTO_GIFT_PROPERTY_VALUE) return { meta: null };
    if (raw[0] !== 'm') return null;

    const meta = Number(raw.slice(1));

    return Number.isFinite(meta) ? { meta } : null;
  };

  const itemMarker = (item) => parseMarker(item?.properties?.[AUTO_GIFT_PROPERTY_KEY]);

  const isAutoGiftItem = (item) => itemMarker(item) !== null;

  const slotMarkerValue = (slot) => {
    return slot.meta === null ? String(giftConfig.propertyValue ?? 'true') : `m${slot.meta}`;
  };

  const productIdFromGid = (gid) => Number(String(gid ?? '').split('/').pop());

  // A config embutida pela barra (re-renderizada via section rendering) é sempre
  // a mais fresca; o global Theme.consoGiftProgress.config congela no primeiro
  // page load e fica obsoleto se a config mudar na console no meio da sessão.
  const getFreshConfig = () => {
    const configElement = document.querySelector('[data-conso-gift-config]');

    if (configElement) {
      try {
        const domConfig = JSON.parse(configElement.textContent);
        if (parseGiftTiers(domConfig).length > 0) return domConfig;
      } catch {
        // JSON inválido: cai no fallback do page load
      }
    }

    return initialConfig;
  };

  // Vagas de brinde do carrinho no total atual: uma por tier atingido no modo
  // cumulativo (cada uma com o pool DAQUELE tier), e uma só, com o pool unido,
  // fora dele — onde o cliente pode preferir um brinde de tier inferior.
  const getSlots = (qualifyingTotal) => {
    const config = getFreshConfig();
    const atingidos = parseGiftTiers(config).filter((tier) => qualifyingTotal >= Number(tier.metaCents));

    if (atingidos.length === 0) return [];

    if (config?.cumulativo === true) {
      return atingidos.map((tier) => ({ meta: Number(tier.metaCents), brindes: tier.brindes ?? [] }));
    }

    return [{ meta: null, brindes: atingidos.flatMap((tier) => tier.brindes ?? []) }];
  };

  const slotHasProduct = (slot, productId) => {
    return slot.brindes.some((brinde) => productIdFromGid(brinde.productId) === Number(productId));
  };

  const getAvailableSlotVariants = (slot) => {
    const brindes = slot.brindes.filter((brinde) => getAvailableVariants(brinde).length > 0);

    return brindes.length === 1 ? getAvailableVariants(brindes[0]) : [];
  };

  // Casa cada linha de brinde com uma vaga. Quem declara a meta escolhe
  // primeiro: na ordem inversa, uma linha sem tier tomaria a vaga de quem a
  // declarou e o brinde declarado ficaria pago (é a mesma ordem da function).
  const matchGifts = (giftItems, slots) => {
    const claims = new Map();
    const invalid = [];
    const ordenados = [...giftItems].sort((a, b) => {
      return Number(itemMarker(a)?.meta === null) - Number(itemMarker(b)?.meta === null);
    });

    for (const item of ordenados) {
      const marker = itemMarker(item);
      const index = slots.findIndex((slot, posicao) => {
        if (claims.has(posicao)) return false;
        if (marker.meta !== null && slot.meta !== null && slot.meta !== marker.meta) return false;

        return slotHasProduct(slot, item.product_id);
      });

      if (index === -1) {
        invalid.push(item);
      } else {
        claims.set(index, item);
      }
    }

    return { claims, invalid };
  };

  // Estoque vivo por produto (snippet conso-brinde-estoque). Como a config, o
  // embutido na barra é mais fresco que o global do page load.
  const getFreshEstoque = () => {
    const estoqueElement = document.querySelector('[data-conso-gift-estoque]');

    if (estoqueElement) {
      try {
        return JSON.parse(estoqueElement.textContent) ?? {};
      } catch {
        // JSON inválido: cai no global
      }
    }

    return giftConfig?.estoque ?? {};
  };

  // O `disponivel` da config é uma foto do dia em que /app/brindes salvou; só
  // vale quando o produto não veio no estoque vivo.
  const getAvailableVariants = (brinde) => {
    const vivas = getFreshEstoque()[String(productIdFromGid(brinde?.productId))];

    if (Array.isArray(vivas)) {
      return vivas.filter((variant) => variant?.variantIdNumerico);
    }

    return (brinde?.variants ?? []).filter((variant) => variant?.disponivel && variant?.variantIdNumerico);
  };

  // Elegibilidade considera o total pós-todos-descontos (inclusive cupons de pedido).
  // Usa cart.total_price e desconta a linha do brinde, pois o cliente só "ganha" o
  // brinde se o que ele efetivamente paga pelos demais itens atinge a meta.
  const getQualifyingTotal = (cart) => {
    const total = Number(cart.total_price ?? 0);
    let brindeLinePrice = 0;

    for (const item of cart.items ?? []) {
      if (isAutoGiftItem(item)) {
        brindeLinePrice += Number(item.final_line_price ?? 0);
      }
    }

    return Math.max(0, total - brindeLinePrice);
  };

  const getAutoGiftItems = (cart) => {
    return (cart.items ?? []).filter(isAutoGiftItem);
  };

  const getCartSectionIds = () => {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.getAttribute('data-section-id'))
      .filter(Boolean)
      .filter((sectionId, index, allSectionIds) => allSectionIds.indexOf(sectionId) === index);
  };

  const getSyncedQualifyingTotal = (cart) => {
    return Number(cart?.attributes?.[QUALIFYING_TOTAL_ATTRIBUTE_KEY] ?? NaN);
  };

  const hasCartPayload = (cart) => {
    return Boolean(cart && Array.isArray(cart.items));
  };

  const fetchCart = async () => {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }

    return response.json();
  };

  const syncQualifyingTotalAttribute = async (cart, qualifyingTotal) => {
    if (!QUALIFYING_TOTAL_ATTRIBUTE_KEY) return cart;

    if (getSyncedQualifyingTotal(cart) === qualifyingTotal) {
      return cart;
    }

    const sectionIds = getCartSectionIds();
    const body = {
      attributes: {
        [QUALIFYING_TOTAL_ATTRIBUTE_KEY]: String(qualifyingTotal),
      },
    };

    if (sectionIds.length > 0) {
      body.sections = sectionIds;
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Failed to sync gift qualifying total: ${response.status}`);
    }

    return response.json();
  };

  const addGiftToCart = async (variantIdNumerico, markerValue) => {
    const requestBody = {
      id: Number(variantIdNumerico),
      quantity: 1,
      properties: {
        [AUTO_GIFT_PROPERTY_KEY]: markerValue,
      },
    };

    const sectionIds = getCartSectionIds();

    if (sectionIds.length > 0) {
      requestBody.sections = sectionIds.join(',');
      requestBody.sections_url = window.location.pathname;
    }

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status) {
      throw new Error(responseData.description || responseData.message || `Failed to add gift: ${response.status}`);
    }

    return responseData;
  };

  const removeGiftFromCart = async (lineKey) => {
    const requestBody = {
      id: lineKey,
      quantity: 0,
    };

    const sectionIds = getCartSectionIds();

    if (sectionIds.length > 0) {
      requestBody.sections = sectionIds.join(',');
      requestBody.sections_url = window.location.pathname;
    }

    const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status) {
      throw new Error(responseData.description || responseData.message || `Failed to remove gift: ${response.status}`);
    }

    return responseData;
  };

  const dispatchCartUpdate = (cart, sections) => {
    // Este 'cart:update' é de segundo plano (não é um add-to-cart do usuário).
    // O cart drawer trata qualquer 'cart:update' como add e abriria sozinho com
    // auto_open ligado; a flag faz o guard (conso-cart-drawer-guard.js) suprimir
    // a abertura. dispatchEvent é síncrono, então limpamos logo após.
    window.__consoSilentCartUpdate = true;
    try {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: AUTO_GIFT_SOURCE,
            data: {
              itemCount: Number(cart?.item_count ?? 0),
              source: AUTO_GIFT_SOURCE,
              sections,
            },
          },
        })
      );
    } finally {
      window.__consoSilentCartUpdate = false;
    }
  };

  const getSectionsFromCartPayload = (cart) => {
    return cart?.sections && typeof cart.sections === 'object' ? cart.sections : undefined;
  };

  const syncGiftPresence = async (eventCart = null) => {
    // Um sync em andamento decide em cima do carrinho de quando ele comecou.
    // Devolver a promise dele e parar por aqui DESCARTA a mudanca que acabou de
    // acontecer: era o que deixava o brinde no carrinho depois de o total cair
    // abaixo da meta. Como o proprio script dispara evento ao add/remover, os
    // gatilhos se atropelam o tempo todo. Marca pra rodar de novo no fim.
    if (syncInFlight) {
      syncPendente = true;
      return syncInFlight;
    }

    syncInFlight = (async () => {
      let cart = hasCartPayload(eventCart) ? eventCart : await fetchCart();
      const qualifyingTotal = getQualifyingTotal(cart);
      const previousQualifyingTotal = getSyncedQualifyingTotal(cart);

      cart = await syncQualifyingTotalAttribute(cart, qualifyingTotal);

      const qualifyingTotalChanged = previousQualifyingTotal !== qualifyingTotal;
      const slots = getSlots(qualifyingTotal);
      const { claims, invalid } = matchGifts(getAutoGiftItems(cart), slots);

      // Remove brindes sem vaga: o total caiu abaixo da meta, o pool mudou na
      // console do app, ou a vaga que a linha reivindica já é de outra linha.
      if (invalid.length > 0) {
        let lastResponse = null;

        for (const item of invalid) {
          lastResponse = await removeGiftFromCart(item.key);
        }

        const updatedCart = hasCartPayload(lastResponse) ? lastResponse : await fetchCart();
        dispatchCartUpdate(updatedCart, lastResponse?.sections || getSectionsFromCartPayload(updatedCart));
        return;
      }

      // Auto-add apenas onde a escolha é unívoca: a vaga tem um único produto
      // com uma única variante disponível. Havendo opções, o seletor da barra
      // decide. Uma vaga por passada — o evento do add reagenda o sync, que
      // preenche a próxima.
      //
      // Varre TODAS as vagas livres, não só a primeira: com o tier de baixo
      // exigindo escolha (ex.: meias ou cueca) e o de cima com brinde único, a
      // busca pela primeira vaga livre parava na ambígua e o brinde do tier de
      // cima nunca entrava sozinho — o cliente só o via depois de escolher o de
      // baixo, ou nunca, se ignorasse a escolha.
      const vagaAuto = slots
        .map((slot, posicao) => ({ slot, posicao }))
        .filter(({ posicao }) => !claims.has(posicao))
        .map(({ slot }) => ({ slot, variantes: getAvailableSlotVariants(slot) }))
        .find(({ variantes }) => variantes.length === 1);

      if (vagaAuto) {
        const addResponse = await addGiftToCart(
          vagaAuto.variantes[0].variantIdNumerico,
          slotMarkerValue(vagaAuto.slot)
        );
        const updatedCart = hasCartPayload(addResponse) ? addResponse : await fetchCart();

        dispatchCartUpdate(updatedCart, addResponse.sections || getSectionsFromCartPayload(updatedCart));
        return;
      }

      if (qualifyingTotalChanged) {
        dispatchCartUpdate(cart, getSectionsFromCartPayload(cart));
      }
    })()
      .catch((error) => {
        console.error('[conso-cart-gift-auto-add]', error);
      })
      .finally(() => {
        queuedCart = null;
        syncInFlight = null;

        // Reagenda em vez de chamar direto: scheduleSync limpa o timer pendente,
        // entao o gatilho engolido e um que tenha chegado depois viram uma
        // passada so. Converge, porque a re-rodada so acontece quando algo
        // mudou durante a anterior.
        if (syncPendente) {
          syncPendente = false;
          scheduleSync();
        }
      });

    return syncInFlight;
  };

  const scheduleSync = (cart = null) => {
    if (hasCartPayload(cart)) {
      queuedCart = cart;
    }

    if (scheduledSync) {
      window.clearTimeout(scheduledSync);
    }

    scheduledSync = window.setTimeout(() => {
      void syncGiftPresence(queuedCart);
    }, 120);
  };

  // Seletor de brinde renderizado pela barra de progresso (Liquid): botões com
  // data-conso-gift-add (+ select opcional de variante) e data-conso-gift-remove.
  document.addEventListener('click', async (event) => {
    if (!(event.target instanceof Element)) return;

    const addButton = event.target.closest('[data-conso-gift-add]');
    const removeButton = event.target.closest('[data-conso-gift-remove]');

    if (!addButton && !removeButton) return;

    event.preventDefault();

    if (manualActionInFlight) return;
    manualActionInFlight = true;

    const actionButton = addButton ?? removeButton;
    actionButton.setAttribute('disabled', 'disabled');

    try {
      const cart = await fetchCart();

      if (addButton) {
        const optionRoot = addButton.closest('[data-conso-gift-option]');
        const select = optionRoot?.querySelector('[data-conso-gift-variant-select]');
        const variantId = Number(select?.value || addButton.getAttribute('data-conso-gift-add'));

        if (!variantId) return;

        // A vaga do botão é revalidada contra o carrinho fresco: entre o render
        // da barra e o clique ela pode ter sido preenchida (outra aba, auto-add).
        const slotAlvo = addButton.getAttribute('data-conso-gift-slot') ?? '';
        const slots = getSlots(getQualifyingTotal(cart));
        const { claims } = matchGifts(getAutoGiftItems(cart), slots);
        const vaga = slots.find((slot, posicao) => {
          return !claims.has(posicao) && String(slot.meta ?? '') === slotAlvo;
        });

        if (!vaga) return;

        const addResponse = await addGiftToCart(variantId, slotMarkerValue(vaga));
        const updatedCart = hasCartPayload(addResponse) ? addResponse : await fetchCart();
        dispatchCartUpdate(updatedCart, addResponse.sections || getSectionsFromCartPayload(updatedCart));
        return;
      }

      // No cumulativo há mais de um brinde no carrinho: o botão diz qual trocar.
      const chaveAlvo = removeButton.getAttribute('data-conso-gift-remove');
      const giftItems = getAutoGiftItems(cart);
      const giftItem = chaveAlvo ? giftItems.find((item) => item.key === chaveAlvo) : giftItems[0];

      if (!giftItem) return;

      const removeResponse = await removeGiftFromCart(giftItem.key);
      const updatedCart = hasCartPayload(removeResponse) ? removeResponse : await fetchCart();
      dispatchCartUpdate(updatedCart, removeResponse.sections || getSectionsFromCartPayload(updatedCart));
    } catch (error) {
      console.error('[conso-cart-gift-auto-add]', error);
    } finally {
      manualActionInFlight = false;
      actionButton.removeAttribute('disabled');
    }
  });

  // Os dois eventos legados abaixo so existem porque OS PROPRIOS scripts conso
  // ainda os disparam (ver conso-cart-event-bridge.js). Na Horizon 4 nada nativo
  // emite 'cart:update'/'discount:update': quem mexe no carrinho — add-to-cart,
  // seletor de quantidade, remover linha, cupom — dispara os Standard Events
  // 'shopify:cart:*'. Sem escutar tambem os v4, o sync so rodava no page load e
  // nas acoes deste script: o brinde do 2o tier so aparecia depois de um F5, e
  // os brindes nao saiam quando o carrinho caia abaixo da meta.
  //
  // O Standard Event entrega o carrinho numa promise; scheduleSync() sem payload
  // de proposito, que o GET em /cart.js sai mais barato que depender desse
  // contrato. O debounce de 120ms junta o par legado+v4 numa passada so.
  document.addEventListener('cart:update', (event) => {
    scheduleSync(event.detail?.resource);
  });
  document.addEventListener('discount:update', (event) => {
    scheduleSync(event.detail?.resource);
  });
  document.addEventListener('shopify:cart:lines-update', () => {
    scheduleSync();
  });
  document.addEventListener('shopify:cart:discount-update', () => {
    scheduleSync();
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      scheduleSync();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
  } else {
    scheduleSync();
  }
}
